import { useCallback, useState } from 'react';
import { ApiError } from '@cbhq/data-layer';
import { useGetListOrganizationDomainClaims } from '@cbhq/identity-api-hooks/account-management/fetch';

import { useOrganization } from '../../../contexts/OrganizationContext';
import { logWarning } from '../../../utils/logging';

/**
 * Data hook for the Claim a domain page. Fetches every domain the selected
 * organization has claimed.
 */
export function useDomainClaimsData() {
  const { selectedOrganization, hasRequestedOrganizationAccessDenied } = useOrganization();
  const [isPermissionDenied, setIsPermissionDenied] = useState(false);

  const organizationId = hasRequestedOrganizationAccessDenied
    ? ''
    : selectedOrganization?.organizationId ?? '';

  const skip = organizationId.length === 0;

  // ListOrganizationDomainClaims requires org-level admin or account_management
  // admin on the org, which is narrower than the roles that populate the org
  // picker (Prime/CDP/Coinbiz/Retail admins can select the org but still get a
  // 403 here). Surface that as a friendly fallback instead of the generic
  // error boundary. Deferred via queueMicrotask since onError fires during
  // render.
  const handleError = useCallback(
    (err: Error) => {
      if (err instanceof ApiError && err.status === 403) {
        logWarning(err, { context: 'useDomainClaimsData', isHandled: true, organizationId });
        queueMicrotask(() => setIsPermissionDenied(true));
        return;
      }
      throw err;
    },
    [organizationId],
  );

  const result = useGetListOrganizationDomainClaims(
    { organizationId },
    { skip, returnStaleDataOnError: true, onError: handleError },
  );

  return { domainClaims: result?.domainClaims ?? [], isPermissionDenied };
}
